import React, { useCallback, useEffect, useRef } from 'react'
import { LineChartLoader } from 'views/Info/components/ChartLoaders'
import { createChart } from 'lightweight-charts'
import useTheme from 'hooks/useTheme'
import { alpha } from '@mui/material/styles'
import styled from 'styled-components'
import { useMatchBreakpoints } from '@pancakeswap/uikit'

const ChartContainer = styled.div`
  position: relative;
`

const ChartError = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => alpha(theme.colors.background, 0.05)};
  color: ${({ theme }) => theme.colors.text};
  backdrop-filter: blur(4px);
  z-index: 5;
`

/**
 * Note: remember that it needs to be mounted inside the container with fixed height
 */
const PriceTradingChart = ({
  setHoverValue,
  setHoverDate,
  isChangePositive,
  chartData,
}: {
  setHoverValue?: (value?: number) => void
  setHoverDate?: (value?: string) => void
  isChangePositive?: boolean
  chartData?: any
}) => {
  const { theme } = useTheme()
  const { isMobile } = useMatchBreakpoints()
  const chartHeight = isMobile ? 420 : 340

  const chartContainerRef = useRef()

  const onTimeMove = useCallback(
    (param: any) => {
      if (!param.point) return
      const priceValue = param.seriesPrices.values().next().value
      if (priceValue?.open) setHoverValue(Number(priceValue?.open))
      else setHoverValue(Number(priceValue))
      setHoverDate(
        new Date(param?.time * 1000).toLocaleString('us', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        }),
      )
    },
    [setHoverDate, setHoverValue],
  )

  const { data } = chartData

  useEffect(() => {
    if (!chartContainerRef.current) return

    const options = {
      height: chartHeight,
      alignLabels: true,
      autoSize: true,
      timeScale: {
        // rightOffset: 12,
        // barSpacing: 3,
        // fixLeftEdge: true,
        // lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll: true,
        // borderVisible: false,
        borderColor: alpha(theme.colors.text, 0.05),
        visible: true,
        timeVisible: true,
        secondsVisible: false,
      },
      rightPriceScale: {
        borderColor: alpha(theme.colors.text, 0.05),
      },
      grid: { horzLines: { color: alpha(theme.colors.text, 0.05) }, vertLines: { color: 'transparent' } },
      layout: { backgroundColor: alpha(theme.colors.text, 0), textColor: alpha(theme.colors.text, 0.3) },
    }
    const chart = createChart(chartContainerRef.current, options)
    chart.timeScale().fitContent()
    chart.subscribeCrosshairMove(onTimeMove)

    const handleResize = () => {
      if (chartContainerRef?.current?.clientWidth)
        chart.applyOptions({ width: chartContainerRef?.current?.clientWidth })
    }
    if (data?.[0]) {
      let precision
      if (data?.[0]?.open) {
        precision = Math.floor(Math.log10(1 / data[data.length - 1].open) + 2)
        if (precision < 0) precision = 0
        const candleSeries = chart.addCandlestickSeries({})
        candleSeries.applyOptions({
          priceFormat: { precision: precision + 1, minMove: Number(1 / 10 ** (precision + 1)) },
        })
        candleSeries.setData(data)
      }
      if (data?.[0]?.value) {
        precision = Math.floor(Math.log10(1 / data[data.length - 1].value) + 2)
        if (precision < 0) precision = 0
        const lineSeries = chart.addLineSeries({
          lineWidth: 1,
          color: theme.colors.text,
          priceLineColor: isChangePositive ? theme.colors.success : theme.colors.failure,
        })
        lineSeries.setData(data)
        lineSeries.applyOptions({
          priceFormat: { type: 'price', precision: precision + 1, minMove: Number(1 / 10 ** (precision + 1)) },
        })
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.unsubscribeCrosshairMove(onTimeMove)
      chart.remove()
    }
  }, [
    data,
    onTimeMove,
    theme.colors.text,
    theme.colors.failure,
    theme.colors.success,
    isChangePositive,
    isMobile,
    chartHeight,
  ])

  if (!chartData || chartData.status === 'loading') {
    return <LineChartLoader />
  }

  return (
    <>
      <ChartContainer
        ref={chartContainerRef}
        style={{ width: '100%', height: chartHeight }}
        onMouseLeave={() => {
          if (setHoverDate) setHoverDate(undefined)
          if (setHoverValue) setHoverValue(undefined)
        }}
      >
        {chartData?.data?.length === 0 && (
          <ChartError>
            Price data is temporally unavailable.
            <br />
            Please try again after a while,
            <br />
            or select another time option.
          </ChartError>
        )}
      </ChartContainer>
    </>
  )
}

export default PriceTradingChart
